import { createSelector, DefaultProjectorFn, MemoizedSelector } from '@ngrx/store';

import {
  BundleUpdateProcessTypeEnum,
  BundleUpdateProgressEnum,
  BundleUpdateStatusEnum,
  BundleVersion,
  ComponentVerification,
  ComponentVerificationBoolean,
  DeviceBusTopologyProperties,
  Feature,
  FeatureNames,
  MissingBusTopology,
  VersionListParams,
  XotaBundleUpdateStep,
  XotaOperationStatus,
} from '@vi-iot/client';

import { isReady } from '../_common';
import { AppState } from '../app.state';
import { AuthSelectors } from '../auth';
import { DevicesSelectors } from '../devices';

import { InstallationDetailsSelectors } from '../installation-details';
import { InstallationFeaturesSelectors } from '../installation-features/installation-features.selectors';
import { FirmwareUpdateStatusStartConstraints, FirmwareUpdateStep } from './firmware-update.model';
import { FirmwareUpdateState } from './firmware-update.state';
import { RemoteConfigSelectors } from '../remote-config';

export namespace FirmwareUpdateSelectors {
  export const firmwareUpdate = (state: AppState): FirmwareUpdateState => state.firmwareUpdate;

  export const selectStep = createSelector(
    firmwareUpdate,
    (state): FirmwareUpdateStep => state.step
  );

  export const selectBundleUpdateStep = createSelector(
    firmwareUpdate,
    (state): XotaBundleUpdateStep => state.bundleUpdateStep
  );

  export const selectProgress = createSelector(
    firmwareUpdate,
    (state): BundleUpdateProgressEnum => state.progress
  );

  export const selectStatus = createSelector(
    firmwareUpdate,
    (state): BundleUpdateStatusEnum => state.status
  );

  export const selectProcessType = createSelector(
    firmwareUpdate,
    (state): BundleUpdateProcessTypeEnum => state.processType
  );

  export const selectIsLoading = createSelector(firmwareUpdate, (state): boolean => state.loading);

  export const selectIsInUpdateTimeblock = createSelector(
    firmwareUpdate,
    (state): boolean => state.isInUpdateTimeBlock
  );

  export const selectIsUpdateAvailable = createSelector(
    firmwareUpdate,
    (state): boolean => state.updateIsAvailable
  );

  export const selectIsCheckUpdateFailure = createSelector(
    firmwareUpdate,
    (state): boolean => state.checkUpdateFailure
  );

  export const selectComponentVerification = createSelector(
    firmwareUpdate,
    (state): ComponentVerificationBoolean => state.componentVerification
  );

  export const selectErrorExtendedPayload = createSelector(
    firmwareUpdate,
    (state): ComponentVerification => state.error?.extendedPayload
  );

  export const selectOverallProgress = createSelector(
    firmwareUpdate,
    (state): number => state.overallProgress
  );

  export const selectTargetBundleVersion = createSelector(
    firmwareUpdate,
    (state): BundleVersion[] => state.targetBundleVersion
  );

  export const selectCurrentBundleVersion = createSelector(
    firmwareUpdate,
    (state): BundleVersion[] => state.currentBundleVersion
  );

  export const selectSalesForceCaseNumber = createSelector(
    firmwareUpdate,
    (state): string => state.salesForceCaseNumber
  );

  export const selectMissingBusTopology = createSelector(
    firmwareUpdate,
    (state): MissingBusTopology => state.missingBusTopology
  );

  export const selectVersionListParams = createSelector(
    selectProcessType,
    selectProgress,
    selectStatus,
    selectOverallProgress,
    selectMissingBusTopology,
    selectCurrentBundleVersion,
    selectTargetBundleVersion,
    selectIsLoading,
    (
      processType,
      progress,
      status,
      overallProgress,
      missingBusTopology,
      currentBundleVersion,
      targetBundleVersion,
      isLoading
    ): VersionListParams => ({
      processType,
      progress,
      status,
      overallProgress,
      missingBusTopology,
      currentBundleVersion,
      targetBundleVersion,
      isLoading,
    })
  );

  export const selectXotaOperationStatus = createSelector(
    firmwareUpdate,
    (state): XotaOperationStatus => state.xotaOperationStatus
  );

  export const isFirmwareUpdateButtonAvailable = (): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > => {
    return createSelector(
      DevicesSelectors.isE3HeatpumpDevice(),
      InstallationFeaturesSelectors.selectInstallationGatewayFeature(
        FeatureNames.gatewayRemoteDiagnostics
      ),
      AuthSelectors.isXotaApplicationRole(),
      InstallationDetailsSelectors.getGatewayStatus(),
      DevicesSelectors.selectIsSelectedDeviceVentilationDevice,
      (
        isE3Heatpump,
        feature,
        isXotaApplicationRole,
        gatewayStatus,
        isVentilationDevice
      ): boolean => {
        return (
          (isE3Heatpump || isVentilationDevice) &&
          isReady(feature) &&
          isXotaApplicationRole &&
          !gatewayStatus.offline
        );
      }
    );
  };

  export function selectFirmwareUpdateStatusStartConstraints(): MemoizedSelector<
    AppState,
    FirmwareUpdateStatusStartConstraints,
    DefaultProjectorFn<FirmwareUpdateStatusStartConstraints>
  > {
    return createSelector(
      isSoftwareVersionAllowingFirmwareUpdate(),
      selectIsEveryRequestSuccessful(),
      selectIsGatewayAllowed(),
      (
        isSoftwareCorrect,
        isEveryRequestSuccessful,
        isGatewayAllowed
      ): FirmwareUpdateStatusStartConstraints => {
        const canProceed = isSoftwareCorrect && isGatewayAllowed && isEveryRequestSuccessful;

        return {
          canProceed,
          isSoftwareCorrect,
          isGatewayAllowed,
          isEveryRequestSuccessful,
        };
      }
    );
  }

  export function isSoftwareVersionAllowingFirmwareUpdate(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationDeviceFeature<
        DeviceBusTopologyProperties,
        never
      >(FeatureNames.deviceBusTopology),
      RemoteConfigSelectors.selectXOTAFirmwareVersionBlackList,
      (feature: Feature<DeviceBusTopologyProperties, never>, xOTAFirmwareVersionBlackList) => {
        if (xOTAFirmwareVersionBlackList) {
          const topology = feature?.properties?.value?.value;
          if (Array.isArray(topology)) {
            const isFobiddenVersionPresent = topology.some((topologyElement) =>
              xOTAFirmwareVersionBlackList.some((forbiddenVersion) =>
                topologyElement.softwareVersion.startsWith(forbiddenVersion)
              )
            );

            return !isFobiddenVersionPresent;
          } else {
            return false;
          }
        } else {
          return true;
        }
      }
    );
  }

  export function selectIsGatewayAllowed(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      firmwareUpdate,
      (state: FirmwareUpdateState): boolean => state.gatewayVerification.value
    );
  }

  export function selectShowFirmwareUpdateStart(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      firmwareUpdate,
      (state: FirmwareUpdateState): boolean => !state.gatewayVerification.loading
    );
  }

  export function selectIsEveryRequestSuccessful(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      firmwareUpdate,
      (state: FirmwareUpdateState): boolean => !state.gatewayVerification.error
    );
  }
}
